import {
  alternativeProductCardStyles,
  highlightedAlternativeProductCardStyles
} from '@components/AlternativesOverlay/AlternativeProductCard.styles';
import ProsAndCons from '@components/ProsAndCons/ProsAndCons';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Link,
  List,
  ListItem,
  NumberStepInput
} from '@coolblue-development/freddy';
import EnergyLabel from '@coolblue-development/next-energy-label/client';
import { MiniProductPage } from '@coolblue-development/next-mini-product-page/client';
import {
  ColorPicker,
  ProductCard,
  ProductTitle,
  ProductTitleReview,
  ProductTitleTitle
} from '@coolblue-development/next-patterns/client';
import { PromotionalProductImage } from '@coolblue-development/next-patterns/core';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import { useAccessoryProductCardContext } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import { useCustomerContext } from '@features/add-to-cart-page/components/Sections/CustomerContext/CustomerContext';
import {
  AccessorySlotHighlightedAlternativeSlot,
  AccessorySlotHighlightedAlternativeSlotProduct,
  AccessorySlotProduct,
  AlternativeProduct,
  Product
} from '@features/add-to-cart-page/typings/Product';
import { USBCChargerIncluded } from '@features/shared/USBCChargerIncluded';
import { useTranslation } from '@i18n/useTranslation';
import { ReactElement, useEffect, useRef } from 'react';

export const HighlightedAlternativeCard = ({
  slot,
  isMediumBreakpointOrUp,
  onClick,
  mainProductId,
  accessoryProductCardIndex,
  accessoryProductCardTotalIndices,
  isAutomated
}: {
  slot: AccessorySlotHighlightedAlternativeSlot;
  isMediumBreakpointOrUp: boolean | undefined;
  onClick: (quantity?: number) => void;
  mainProductId: number;
  accessoryProductCardIndex: number;
  accessoryProductCardTotalIndices: number;
  isAutomated: boolean;
}): ReactElement => {
  const { isInternalUser } = useRequestContext();
  const { isBusinessCustomer } = useCustomerContext();
  const ref = useRef(null);
  const context = useAccessoryProductCardContext();
  const { t } = useTranslation('webshop');
  const highlightedSlotProduct: AccessorySlotHighlightedAlternativeSlotProduct = slot.products[0];
  const selectedColorIndex = context.selectedColorAlternatives?.[highlightedSlotProduct.product.id];
  const highlightedProduct: AlternativeProduct =
    (selectedColorIndex !== undefined &&
      highlightedSlotProduct.product.colorAlternatives &&
      highlightedSlotProduct.product.colorAlternatives[selectedColorIndex]?.product) ||
    highlightedSlotProduct.product;
  const quantityRef = useRef(context.selectedProductQuantity || 1);
  const usbCChargerIncluded =
    highlightedProduct?.hasUsbCChargerIncluded !== null ? (
      <USBCChargerIncluded hasUsbCChargerIncluded={highlightedProduct?.hasUsbCChargerIncluded} />
    ) : undefined;

  useEffect(() => {
    quantityRef.current = context.selectedProductQuantity;
  }, [context.selectedProductQuantity]);

  useEffect(() => {
    if (ref.current) {
      (ref.current as HTMLElement).addEventListener('click', () => onClick(quantityRef.current));
    }
    // A single run is required when the component is rendered, hence the empty array for dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectColorAlternative = (index: string) => {
    context.setSelectedProductAlternative(
      highlightedSlotProduct.product.colorAlternatives?.[+index].product?.id as number
    );
    context.updateSelectedColorAlternatives(highlightedSlotProduct.product.id, +index);
  };

  const quantitySelector = (
    <Flex alignItems='center' gap={200} flex={1}>
      <FormLabel>{t('addToCart.quantity')}</FormLabel>
      <NumberStepInput
        value={isBusinessCustomer ? context.selectedProductQuantity || 1 : undefined}
        min={1}
        onChange={quantity =>
          context.setSelectedProductQuantity(quantity, highlightedProduct?.orderQuantityLimit)
        }
        aria-label={t('addToCart.productQuantity', {
          productName: highlightedProduct.name
        })}
        max={highlightedProduct.orderQuantityLimit || undefined}
      />
    </Flex>
  );

  return (
    <ProductCard.Alternative
      ref={ref}
      fullWidth
      orientation={isMediumBreakpointOrUp ? 'vertical' : 'horizontal'}
      sx={highlightedAlternativeProductCardStyles}
      energyLabel={
        highlightedProduct?.energyLabel ? (
          <EnergyLabel
            // TODO add energy label variation parameter
            level={highlightedProduct.energyLabel.level}
            size='medium'
            direction='right'
            specificationUrl={highlightedProduct.energyLabel.specification?.absoluteUrl}
          />
        ) : undefined
      }
      highlightedCardTitle={slot.title}
      productId={highlightedProduct.id}
      productTitle={
        highlightedProduct?.name ? (
          <ProductTitle>
            <ProductTitleTitle>
              <MiniProductPage
                productId={highlightedProduct.id}
                subFeature={isAutomated ? 'recommended_accessories' : 'product_type_section'}
                mainProductId={mainProductId}
                index={accessoryProductCardIndex}
                totalIndices={accessoryProductCardTotalIndices}
                ctaRef={ref}
                customCta={
                  <Button variant='action' fullWidth={isBusinessCustomer}>
                    {t('alternativesOverlay.selectThisOption')}
                  </Button>
                }
                isBusinessCustomer={isBusinessCustomer}
                quantity={isBusinessCustomer ? quantitySelector : undefined}
              >
                {highlightedProduct.name}
              </MiniProductPage>
            </ProductTitleTitle>
            {isInternalUser && highlightedProduct.urls && (
              <Link href={highlightedProduct.urls?.product} target='_blank'>
                {t('alternativesOverlay.internalLink')}
              </Link>
            )}
            <ProductTitleReview
              value={highlightedProduct.reviewMetaData?.averageRating || 0}
              reviewCount={highlightedProduct.reviewMetaData?.count}
            />
          </ProductTitle>
        ) : (
          <></>
        )
      }
      prosAndCons={
        // By CMS restriction, description cannot be set at the same time as pros/cons
        <>
          {highlightedSlotProduct?.description}
          {highlightedSlotProduct?.pros && (
            <ProsAndCons pros={highlightedSlotProduct.pros} cons={highlightedSlotProduct.cons} />
          )}
        </>
      }
      usbCChargerIncluded={usbCChargerIncluded}
      salesPrice={
        highlightedProduct.salesPrice && highlightedProduct.salesPrice.includingVat ? (
          <SalesPrice
            size={isBusinessCustomer ? 'small' : 'medium'}
            variant={isBusinessCustomer ? 'business' : undefined}
            personalPrice={highlightedProduct.personalPrice}
            currentPrice={
              <SalesPriceCurrent
                showPricePerItem={isBusinessCustomer}
                price={highlightedProduct.salesPrice.includingVat}
                priceExcludingVat={
                  isBusinessCustomer ? highlightedProduct.salesPrice.excludingVat : undefined
                }
              />
            }
            {...(highlightedProduct.listPrice?.includingVat && {
              formerPrice: (
                <SalesPriceFormer
                  price={highlightedProduct.listPrice.includingVat}
                  prefix={t('orderBlock.salesPrice.prefix')}
                />
              )
            })}
          />
        ) : undefined
      }
      promotionalProductImage={
        highlightedProduct?.primaryImage?.id ? (
          <PromotionalProductImage
            alt={highlightedProduct.name}
            imageId={highlightedProduct.primaryImage.id.toString()}
            size='medium'
          />
        ) : (
          <></>
        )
      }
      colorPicker={
        highlightedSlotProduct.product?.colorAlternatives &&
        highlightedSlotProduct.product?.colorAlternatives.length > 0 ? (
          <ColorPicker
            colorAlternatives={context.sanitizeColorAlternativesData(
              highlightedSlotProduct.product
            )}
            onChange={value => {
              onSelectColorAlternative(value);
            }}
            value={
              context.selectedColorAlternatives[highlightedSlotProduct.product.id]?.toString() ||
              '0'
            }
          />
        ) : (
          <></>
        )
      }
    />
  );
};

export const ProductAlternativeCard = ({
  slot,
  isMediumBreakpointOrUp,
  onClick,
  mainProductId,
  accessoryProductCardIndex,
  accessoryProductCardTotalIndices,
  isAutomated
}: {
  slot: AccessorySlotProduct;
  isMediumBreakpointOrUp: boolean | undefined;
  onClick: (quantity?: number) => void;
  mainProductId: number;
  accessoryProductCardIndex: number;
  accessoryProductCardTotalIndices: number;
  isAutomated: boolean;
}): ReactElement => {
  const alternativeProduct: Product = slot.product;
  const { isInternalUser } = useRequestContext();
  const { isBusinessCustomer } = useCustomerContext();
  const ref = useRef(null);
  const { t } = useTranslation('webshop');
  const context = useAccessoryProductCardContext();
  const quantityRef = useRef(context.selectedProductQuantity || 1);

  const usbCChargerIncluded =
    alternativeProduct?.hasUsbCChargerIncluded !== null ? (
      <USBCChargerIncluded hasUsbCChargerIncluded={alternativeProduct?.hasUsbCChargerIncluded} />
    ) : undefined;

  useEffect(() => {
    quantityRef.current = context.selectedProductQuantity;
  }, [context.selectedProductQuantity]);

  useEffect(() => {
    if (ref.current) {
      (ref.current as HTMLElement).addEventListener('click', () => onClick(quantityRef.current));
    }
    // A single run is required when the component is rendered, hence the empty array for dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quantitySelector = (
    <Flex alignItems='center' gap={200} flex={1}>
      <FormLabel>{t('addToCart.quantity')}</FormLabel>
      <NumberStepInput
        value={isBusinessCustomer ? context.selectedProductQuantity || 1 : undefined}
        min={1}
        onChange={quantity =>
          context.setSelectedProductQuantity(quantity, alternativeProduct?.orderQuantityLimit)
        }
        aria-label={t('addToCart.productQuantity', {
          productName: alternativeProduct.name
        })}
        max={alternativeProduct.orderQuantityLimit || undefined}
      />
    </Flex>
  );

  return (
    <ProductCard.Alternative
      ref={ref}
      fullWidth
      orientation={isMediumBreakpointOrUp ? 'vertical' : 'horizontal'}
      sx={alternativeProductCardStyles}
      energyLabel={
        alternativeProduct.energyLabel ? (
          <EnergyLabel
            // TODO add energy label variation parameter
            level={alternativeProduct.energyLabel.level}
            size='medium'
            direction='right'
            specificationUrl={alternativeProduct.energyLabel.specification?.absoluteUrl}
          />
        ) : (
          <></>
        )
      }
      productId={alternativeProduct.id}
      productTitle={
        alternativeProduct?.name ? (
          <ProductTitle>
            <ProductTitleTitle>
              <MiniProductPage
                productId={alternativeProduct.id}
                subFeature={isAutomated ? 'recommended_accessories' : 'product_type_section'}
                mainProductId={mainProductId}
                index={accessoryProductCardIndex}
                totalIndices={accessoryProductCardTotalIndices}
                ctaRef={ref}
                customCta={
                  <Button fullWidth data-cy='this button'>
                    {t('alternativesOverlay.selectThisOption')}
                  </Button>
                }
                isBusinessCustomer={isBusinessCustomer}
                quantity={isBusinessCustomer ? quantitySelector : undefined}
              >
                {alternativeProduct.name}
              </MiniProductPage>
            </ProductTitleTitle>
            {isInternalUser && alternativeProduct.urls && (
              <Link href={alternativeProduct.urls?.product} target='_blank'>
                {t('alternativesOverlay.internalLink')}
              </Link>
            )}
            <ProductTitleReview
              value={alternativeProduct.reviewMetaData?.averageRating || 0}
              reviewCount={alternativeProduct.reviewMetaData?.count}
            />
          </ProductTitle>
        ) : (
          <></>
        )
      }
      prosAndCons={
        alternativeProduct?.highlights && (
          <List gap='none'>
            {alternativeProduct.highlights.map((highlight: string): ReactElement => {
              return (
                <Box
                  key={highlight}
                  sx={{ li: { listStylePosition: 'outside', marginLeft: '1.6rem' } }}
                >
                  <ListItem>{highlight}</ListItem>
                </Box>
              );
            })}
          </List>
        )
      }
      usbCChargerIncluded={usbCChargerIncluded}
      salesPrice={
        alternativeProduct.salesPrice && alternativeProduct.salesPrice.includingVat ? (
          <SalesPrice
            size={isBusinessCustomer ? 'small' : 'medium'}
            variant={isBusinessCustomer ? 'business' : undefined}
            personalPrice={alternativeProduct.personalPrice}
            currentPrice={
              <SalesPriceCurrent
                showPricePerItem={isBusinessCustomer}
                price={alternativeProduct.salesPrice.includingVat}
                priceExcludingVat={
                  isBusinessCustomer ? alternativeProduct.salesPrice.excludingVat : undefined
                }
              />
            }
            {...(alternativeProduct.listPrice?.includingVat && {
              formerPrice: (
                <SalesPriceFormer
                  price={alternativeProduct.listPrice.includingVat}
                  prefix={t('orderBlock.salesPrice.prefix')}
                />
              )
            })}
          />
        ) : undefined
      }
      promotionalProductImage={
        alternativeProduct?.primaryImage?.id ? (
          <PromotionalProductImage
            alt={alternativeProduct.name}
            imageId={alternativeProduct.primaryImage.id.toString()}
            size='medium'
          />
        ) : (
          <></>
        )
      }
    />
  );
};
